import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Grid,
  Alert,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import PageHeader from '../components/ui/PageHeader'
import LeadFilters from '../components/leads/LeadFilters'
import {
  useLeadSegments,
  useCreateLeadSegment,
  useUpdateLeadSegment,
  useDeleteLeadSegment,
  useSegmentLeadCount,
} from '../hooks/useLeads'
import { LeadSegment, LeadStatus, LeadSource, GSTVerificationStatus } from '../types/lead'
import { leadService } from '../services/leadService'
import { getErrorMessage } from '../utils/errorHandlers'

interface SegmentFilterState {
  status?: LeadStatus[]
  source?: LeadSource[]
  gst_verification_status?: GSTVerificationStatus[]
  location?: string
  city?: string
  state?: string
  interest_category?: string
  campaign_tag?: string
  date_from?: Date | null
  date_to?: Date | null
  search?: string
}

const emptyFilters: SegmentFilterState = {}

const toFilterCriteria = (filters: SegmentFilterState): Record<string, any> => {
  const criteria: Record<string, any> = {}
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value) && value.length === 0) return
    if (key === 'date_from' || key === 'date_to') {
      criteria[key] = (value as Date).toISOString().split('T')[0]
      return
    }
    criteria[key] = value
  })
  return criteria
}

const fromFilterCriteria = (criteria: Record<string, any>): SegmentFilterState => {
  const filters: SegmentFilterState = { ...criteria }
  if (criteria.date_from) filters.date_from = new Date(criteria.date_from)
  if (criteria.date_to) filters.date_to = new Date(criteria.date_to)
  return filters
}

const SegmentLeadCountChip: React.FC<{ segmentId: string }> = ({ segmentId }) => {
  const { data, isLoading } = useSegmentLeadCount(segmentId)
  if (isLoading) return <CircularProgress size={16} />
  return <Chip size="small" label={`${data?.lead_count ?? 0} leads`} />
}

const SegmentsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { data: segments, isLoading } = useLeadSegments()
  const createSegment = useCreateLeadSegment()
  const updateSegment = useUpdateLeadSegment()
  const deleteSegment = useDeleteLeadSegment()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingSegment, setEditingSegment] = useState<LeadSegment | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [filters, setFilters] = useState<SegmentFilterState>(emptyFilters)

  // WhatsApp follow-up dialog state
  const [followupSegment, setFollowupSegment] = useState<LeadSegment | null>(null)
  const [followupTemplateId, setFollowupTemplateId] = useState('')
  const [followupTemplates, setFollowupTemplates] = useState<Array<{ id: string; name: string }>>([])
  const [followupSending, setFollowupSending] = useState(false)
  const [followupResult, setFollowupResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    if (followupSegment) {
      leadService
        .getWhatsAppFollowupTemplates()
        .then(setFollowupTemplates)
        .catch(() => setFollowupTemplates([]))
    }
  }, [followupSegment])

  const openFollowupDialog = (segment: LeadSegment) => {
    setFollowupSegment(segment)
    setFollowupTemplateId('')
    setFollowupResult(null)
  }

  const closeFollowupDialog = () => {
    setFollowupSegment(null)
  }

  const handleSendFollowup = async () => {
    if (!followupSegment || !followupTemplateId) return
    setFollowupSending(true)
    setFollowupResult(null)
    try {
      const result = await leadService.sendSegmentWhatsAppFollowup(followupSegment.id, {
        template_id: followupTemplateId
      })
      setFollowupResult({ success: true, message: result.message })
      enqueueSnackbar(result.message, { variant: 'success' })
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to send WhatsApp follow-up')
      setFollowupResult({ success: false, message })
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setFollowupSending(false)
    }
  }

  const openCreateDialog = () => {
    setEditingSegment(null)
    setName('')
    setDescription('')
    setFilters(emptyFilters)
    setDialogOpen(true)
  }

  const openEditDialog = (segment: LeadSegment) => {
    setEditingSegment(segment)
    setName(segment.name)
    setDescription(segment.description || '')
    setFilters(fromFilterCriteria(segment.filter_criteria))
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
  }

  const handleSave = () => {
    if (!name.trim()) return
    const filter_criteria = toFilterCriteria(filters)

    if (editingSegment) {
      updateSegment.mutate(
        { id: editingSegment.id, data: { name, description: description || null, filter_criteria } },
        { onSuccess: closeDialog }
      )
    } else {
      createSegment.mutate(
        { name, description: description || null, filter_criteria },
        { onSuccess: closeDialog }
      )
    }
  }

  const handleDelete = (segment: LeadSegment) => {
    if (window.confirm(`Delete segment "${segment.name}"?`)) {
      deleteSegment.mutate(segment.id)
    }
  }

  const isSaving = createSegment.isPending || updateSegment.isPending

  return (
    <Box>
      <PageHeader
        title="Lead Segments"
        subtitle="Save and reuse lead filter combinations"
        actions={[
          { label: 'Create Segment', onClick: openCreateDialog, icon: <AddIcon />, variant: 'contained' },
        ]}
      />

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : !segments || segments.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">
                No saved segments yet. Create one to quickly filter leads later.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Lead Count</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {segments.map((segment) => (
                    <TableRow key={segment.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {segment.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {segment.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(segment.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <SegmentLeadCountChip segmentId={segment.id} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Send WhatsApp Follow-up">
                          <IconButton size="small" color="success" onClick={() => openFollowupDialog(segment)}>
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Segment">
                          <IconButton size="small" onClick={() => openEditDialog(segment)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Segment">
                          <IconButton size="small" onClick={() => handleDelete(segment)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSegment ? 'Edit Segment' : 'Create Segment'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Segment Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <LeadFilters
                filters={filters}
                onFilterChange={setFilters}
                onClear={() => setFilters(emptyFilters)}
                onSaveSegment={handleSave}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!name.trim() || isSaving}>
            {isSaving ? 'Saving...' : editingSegment ? 'Update Segment' : 'Save Segment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!followupSegment} onClose={closeFollowupDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Send WhatsApp Follow-up</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sends a WhatsApp message to every lead currently matching{' '}
              <strong>{followupSegment?.name}</strong>.
            </Typography>
            <TextField
              select
              label="Template"
              value={followupTemplateId}
              onChange={(e) => setFollowupTemplateId(e.target.value)}
              fullWidth
            >
              {followupTemplates.length === 0 && (
                <MenuItem disabled value="">
                  No WhatsApp templates found
                </MenuItem>
              )}
              {followupTemplates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            {followupResult && (
              <Alert severity={followupResult.success ? 'success' : 'error'}>{followupResult.message}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFollowupDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            onClick={handleSendFollowup}
            disabled={!followupTemplateId || followupSending}
          >
            {followupSending ? 'Sending...' : 'Send Follow-up'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SegmentsPage
